import React from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { AddonReturn } from "@/types/facility/addon-returns";
import { getColumns } from "./columns";

interface AddonReturnsTableProps {
  data: AddonReturn[];
  isLoading: boolean;
  editingRowId: number | null;
  editingQuantity: number;
  setEditingRowId: (id: number | null) => void;
  setEditingQuantity: (qty: number) => void;
  handleSave: (bookingId: number, addonId: number, quantity: number) => Promise<void>;
  searchCriteria: { sort_key: string; sort_order: string };
  toggleSortOrder: (key: string, currentOrder: string) => void;
}

export default function AddonReturnsTable({
  data,
  isLoading,
  editingRowId,
  editingQuantity,
  setEditingRowId,
  setEditingQuantity,
  handleSave,
  searchCriteria,
  toggleSortOrder,
}: AddonReturnsTableProps) {
  const columns = React.useMemo(
    () => getColumns(
      editingRowId, 
      editingQuantity, 
      setEditingRowId, 
      setEditingQuantity, 
      handleSave, 
      searchCriteria, 
      toggleSortOrder
    ),
    [editingRowId, editingQuantity, setEditingRowId, setEditingQuantity, handleSave, searchCriteria, toggleSortOrder]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="rounded-none border border-border bg-white">
      <ScrollArea className="w-full">
        <Table>
          <TableHeader className="bg-secondary">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className="whitespace-nowrap">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="flex justify-center items-center space-x-2">
                    <div className="size-2 bg-primary rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                    <div className="size-2 bg-primary rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                    <div className="size-2 bg-primary rounded-full animate-bounce"></div>
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                  No results found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
}
